import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import PersonalInformationFormFields from '@/features/onboarding/personal-information/components/fields';
import type { PersonalInformation, PersonalInformationOrDefaults } from '@/features/onboarding/personal-information/schema';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useInputRestriction } from '@/hooks/use-input-restriction';
import { getCountryPrimaryPhoneCode } from '@/services/api/mainstream';

const AREA_CODE_PATTERN = /[0-9+]/;
const PHONE_PATTERN = /[0-9-()]/;

export const PersonalInformationFormFieldsetMobilePhoneLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('personalInformation.mobilePhoneTitle')}</PageHeadingTitle>
        <PageHeadingDescription>{t('personalInformation.mobilePhoneDescription')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
PersonalInformationFormFieldsetMobilePhoneLegend.displayName = 'PersonalInformationFormFieldsetMobilePhoneLegend';

export const PersonalInformationFormFieldsetMobilePhone = () => {
  const form = useFormContext<PersonalInformation>();
  const { defaultValues } = form.formState as { defaultValues: PersonalInformationOrDefaults };
  const { areaCode: defaultAreaCode, countryOfResidency: defaultCountryOfResidency } = defaultValues;
  const countryOfResidency = form.watch('countryOfResidency');
  const { data: countries } = useMainstreamCountries();
  const { t } = useTranslation();
  const [handleAreaCodeKeyDown, handleAreaCodePaste] = useInputRestriction(AREA_CODE_PATTERN);
  const [handlePhoneKeyDown, handlePhonePaste] = useInputRestriction(PHONE_PATTERN);

  useEffect(() => {
    if (defaultAreaCode || defaultCountryOfResidency) return;

    if (countryOfResidency) {
      const country = countries?.[countryOfResidency];
      const phoneCode = country ? getCountryPrimaryPhoneCode(country) : undefined;

      if (phoneCode) {
        form.setValue('areaCode', phoneCode);
        return;
      }
    }

    form.setValue('areaCode', defaultAreaCode);
  }, [countryOfResidency, countries, defaultAreaCode, defaultCountryOfResidency, form.setValue]);

  return (
    <PersonalInformationFormFields className="grid-cols-3">
      <FormField
        control={form.control}
        name="areaCode"
        render={({ field }) => (
          <FormItem className="col-span-1">
            <FormLabel>{t('personalInformation.areaCode')}*</FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={!!defaultValues?.areaCode}
                inputMode="tel"
                placeholder={t('personalInformation.placeholders.areaCode')}
                onKeyDown={handleAreaCodeKeyDown}
                onPaste={(event) => handleAreaCodePaste(event, field.onChange)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="phone"
        render={({ field }) => (
          <FormItem className="col-span-2">
            <FormLabel>{t('personalInformation.mobilePhone')}*</FormLabel>
            <FormControl>
              <Input
                {...field}
                autoComplete="never"
                disabled={!!defaultValues?.phone}
                inputMode="tel"
                placeholder={t('personalInformation.placeholders.phone')}
                onKeyDown={handlePhoneKeyDown}
                onPaste={(event) => handlePhonePaste(event, field.onChange)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </PersonalInformationFormFields>
  );
};
PersonalInformationFormFieldsetMobilePhone.displayName = 'PersonalInformationFormFieldsetMobilePhone';
